import {match, Pattern} from 'ts-pattern';

import {insert, isNil, prop, uniqBy} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import type {
  DashboardArrangementResponseBody,
  DashboardTilePlacementResponseBody,
} from '@dms/api/shared';

import {Nullish} from 'shared/web';

import {DASHBOARD_TILE_CATALOGUE} from '../consts/dashboardTileCatalogue';
import {DASHBOARD_TILE_ID_ALIASES} from '../consts/dashboardTileIdAliases';
import {DASHBOARD_TILE_IDS} from '../consts/dashboardTileIds';
import {DASHBOARD_TILE_ID_SPLITS} from '../consts/dashboardTileIdSplits';
import {DASHBOARD_TILE_SIZES} from '../consts/dashboardTileSizes';
import {DashboardTileCoordinates} from '../types/DashboardTileCoordinates';
import {DashboardTileId} from '../types/DashboardTileId';
import {DashboardTilePlacement} from '../types/DashboardTilePlacement';
import {DashboardTileSize} from '../types/DashboardTileSize';
import {DASHBOARD_GRID_COLUMNS, isDashboardGridPositionValid} from './allocateDashboardGrid';
import {buildDefaultDashboardArrangement} from './buildDefaultDashboardArrangement';
import {isDashboardTileId} from './isDashboardTileId';
import {parseDashboardTileSizeCode} from './parseDashboardTileSizeCode';

const LEGACY_GRID_COLUMNS = 2;

const toCatalogueSize = (id: DashboardTileId, storedSize: DashboardTileSize | null) => {
  const entry = prop(id, DASHBOARD_TILE_CATALOGUE);

  return storedSize !== null && entry.sizes.includes(storedSize) ? storedSize : entry.defaultSize;
};

// Coordinates are kept only when they form a valid, aligned position once scaled to quarter units.
const toStoredCoordinates = (
  tile: DashboardTilePlacementResponseBody,
  size: DashboardTileSize,
  gridColumns: number
): DashboardTileCoordinates | null => {
  if (isNil(tile.column) || isNil(tile.row) || !Number.isInteger(tile.column)) {
    return null;
  }

  const positioned = {
    size,
    column: (tile.column * DASHBOARD_GRID_COLUMNS) / gridColumns,
    row: tile.row,
  };

  return isDashboardGridPositionValid(positioned)
    ? {column: positioned.column, row: positioned.row}
    : null;
};

const toKnownPlacement = (
  tile: DashboardTilePlacementResponseBody,
  gridColumns: number,
  isEnabledById: Record<DashboardTileId, boolean>
): DashboardTilePlacement | null =>
  match(DASHBOARD_TILE_ID_ALIASES.get(tile.id) ?? tile.id)
    .when(isDashboardTileId, (id) => {
      const size = toCatalogueSize(id, parseDashboardTileSizeCode(tile.size, gridColumns));
      // Placements saved before visibility moved into the arrangement carry no `isVisible`.
      const placement = {id, size, isVisible: tile.isVisible ?? prop(id, isEnabledById)};
      const coordinates = toStoredCoordinates(tile, size, gridColumns);

      return isNil(coordinates) ? placement : {...placement, ...coordinates};
    })
    .otherwise(() => null);

// Each part of a split takes half of the composite's width, the first part on the left.
const getLegacySplitPartSize = (compositeSize: DashboardTileSize | null) =>
  match(compositeSize)
    .with('half', () => 'quarter' as const)
    .with('wide', () => 'half' as const)
    .otherwise(() => null);

const splitLegacyPlacement = (
  tile: DashboardTilePlacementResponseBody,
  splitIds: readonly DashboardTileId[],
  isEnabledById: Record<DashboardTileId, boolean>
): DashboardTilePlacement[] => {
  const compositeSize = parseDashboardTileSizeCode(tile.size, LEGACY_GRID_COLUMNS);
  const partSize = getLegacySplitPartSize(compositeSize);
  const getVisibility = (id: DashboardTileId) => tile.isVisible ?? prop(id, isEnabledById);

  if (isNil(compositeSize) || isNil(partSize)) {
    return splitIds.map((id) => ({
      id,
      size: prop(id, DASHBOARD_TILE_CATALOGUE).defaultSize,
      isVisible: getVisibility(id),
    }));
  }

  const coordinates = toStoredCoordinates(tile, compositeSize, LEGACY_GRID_COLUMNS);
  const partColumns = prop(partSize, DASHBOARD_TILE_SIZES).columns;

  return splitIds.map((id, index) => {
    const placement = {id, size: partSize, isVisible: getVisibility(id)};

    return isNil(coordinates)
      ? placement
      : {...placement, column: coordinates.column + index * partColumns, row: coordinates.row};
  });
};

const getExplicitTileIds = (tiles: DashboardTilePlacementResponseBody[]) =>
  new Set(
    tiles.map((tile) => DASHBOARD_TILE_ID_ALIASES.get(tile.id) ?? tile.id).filter(isDashboardTileId)
  );

const toKnownPlacements = (
  tile: DashboardTilePlacementResponseBody,
  gridColumns: number,
  isEnabledById: Record<DashboardTileId, boolean>,
  explicitIds: Set<DashboardTileId>
): DashboardTilePlacement[] => {
  const splitIds =
    gridColumns === LEGACY_GRID_COLUMNS ? DASHBOARD_TILE_ID_SPLITS.get(tile.id) : undefined;

  if (isNotNil(splitIds)) {
    return splitLegacyPlacement(tile, splitIds, isEnabledById).filter(
      (placement) => !explicitIds.has(placement.id)
    );
  }

  const placement = toKnownPlacement(tile, gridColumns, isEnabledById);

  return isNil(placement) ? [] : [placement];
};

const insertAtDefaultPosition = (
  placements: DashboardTilePlacement[],
  id: DashboardTileId,
  cataloguePosition: number
): DashboardTilePlacement[] => {
  const anchorId = DASHBOARD_TILE_IDS.slice(0, cataloguePosition)
    .filter((candidate) => placements.some((placement) => placement.id === candidate))
    .at(-1);

  const insertAt = match(anchorId)
    .with(Pattern.nullish, () => 0)
    .otherwise((id) => placements.findIndex((placement) => placement.id === id) + 1);

  return insert(
    insertAt,
    {id, size: prop(id, DASHBOARD_TILE_CATALOGUE).defaultSize, isVisible: true},
    placements
  );
};

// `isEnabledById` holds the legacy per-widget settings, which seed the visibility of a user who has
// never saved an arrangement. A tile shipped after the user's last save starts visible.
export function reconcileDashboardLayout(
  stored: DashboardArrangementResponseBody | Nullish,
  isEnabledById: Record<DashboardTileId, boolean>
): DashboardTilePlacement[] {
  if (isNil(stored)) {
    return buildDefaultDashboardArrangement().map((placement) => ({
      ...placement,
      isVisible: prop(placement.id, isEnabledById),
    }));
  }

  // Arrangements saved before the quarter grid carry no `gridColumns` and count in board halves.
  const gridColumns =
    stored.gridColumns === DASHBOARD_GRID_COLUMNS ? DASHBOARD_GRID_COLUMNS : LEGACY_GRID_COLUMNS;
  // A split skips every part stored explicitly, wherever it is in the list; among explicit
  // duplicates the first placement wins.
  const explicitIds = getExplicitTileIds(stored.tiles);
  const storedPlacements = uniqBy(
    (placement: DashboardTilePlacement) => placement.id,
    stored.tiles.flatMap((tile) => toKnownPlacements(tile, gridColumns, isEnabledById, explicitIds))
  );

  return DASHBOARD_TILE_IDS.reduce<DashboardTilePlacement[]>(
    (placements, id, cataloguePosition) =>
      placements.some((placement) => placement.id === id)
        ? placements
        : insertAtDefaultPosition(placements, id, cataloguePosition),
    storedPlacements
  );
}
